import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import LazyImage from "@insite/mobius/LazyImage";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";

import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    departments = "departments",
    heading = "heading",
    img = "img",
    description = "description",
    imageAligment = "imageAligment",
}

interface MemberList {
    fields: {
        tittle: string;
        description: string;
        img: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        members: any;
        [fields.departments]: MemberList[];
        [fields.heading]: string;
        [fields.img]: string;
        [fields.description]: string;
        [fields.imageAligment]: "left" | "right";
    };
}

export interface SurCareerBenefitsStyles {
    tittle: InjectableCss;
    heading?: InjectableCss;
    description: InjectableCss;
    box?: InjectableCss;
    container?: InjectableCss;
    icon?: InjectableCss;
    headingContainer?: InjectableCss;
    mainContainer?: InjectableCss;
    ContentImg?: InjectableCss;
    imageWrapper?: InjectableCss;
    mobileContainer?: InjectableCss;
    mobileHeading?: InjectableCss;
    mobileDescription?: InjectableCss;
}

export const surCareerBenefitsStyles: SurCareerBenefitsStyles = {
    tittle: {
        css: css`
            font-family: "Mulish";
            font-style: normal;
            font-weight: 700;
            font-size: 16px;
            line-height: 24px;
            color: ${getColor("text.main")};
            flex: 1 1 50%;
            max-width: 156px;
        `,
    },
    description: {
        css: css`
            font-family: "Mulish";
            font-style: normal;
            font-weight: 400;
            font-size: 14px;
            line-height: 22px;
            color: #545454;
        `,
    },
    box: {
        css: css`
            display: flex;
            gap: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 15px;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    heading: {
        css: css`
            margin-top: 74px;
            margin-bottom: 40px;
            font-family: "Mulish";
            font-style: normal;
            font-weight: 600;
            font-size: 28px;
            line-height: 38px;
            text-transform: uppercase;
            color: ${getColor("text.main")};
        `,
    },
    icon: {
        css: css`
            max-width: 24px;
            max-height: 25px;
        `,
    },
    headingContainer: {
        css: css`
            margin-top: 86px;
            margin-bottom: 58px;
        `,
    },
    mainContainer: {
        css: css`
            border: 1px solid blue;
            max-width: 611px;
            margin: 0 auto;
            /* padding: 0px 41px; */
        `,
    },
    ContentImg: {
        css: css`
            width: 100%;
            img {
                object-fit: contain;
                width: 100%;
                max-width: 420px;
                /* min-width: 420px; */
                aspect-ratio: 1/1;
            }
            /* ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,

                    null,
                    null,
                ])} */
        `,
    },
    imageWrapper: {
        css: css`
            width: 100%;
            height: 100%;
            flex: 0 0 49%;
            max-width: 420px;
        `,
    },
    mobileContainer: {
        css: css`
            padding: 20px;
        `,
    },
    mobileHeading: {
        css: css`
            margin-top: 20px;
            margin-bottom: 20px;
            font-family: "Mulish";
            font-style: normal;
            font-weight: 600;
            font-size: 28px;
            line-height: 38px;
            text-align: center;
            text-transform: uppercase;
            color: ${getColor("text.main")};
        `,
    },
    mobileDescription: {
        css: css`
            margin-top: 10px;
        `,
    },
};

const styles = surCareerBenefitsStyles;

export const SurCareerBenefits: FC<OwnProps> = ({ fields }) => {
    const containerStyles = {
        css: css`
            display: flex;
            max-width: 868px;
            margin: 0 auto;
            padding: 100px 0px;
            gap: 73px;
            flex-direction: ${fields.imageAligment};
        `,
    };

    return (
        <>
            <Hidden below="lg">
                <StyledWrapper {...containerStyles}>
                    <StyledWrapper {...styles.imageWrapper}>
                        <LazyImage
                            src={fields.img}
                            {...styles.ContentImg}
                            altText={fields.heading || "Benefits heading"}
                        />
                    </StyledWrapper>
                    <StyledWrapper>
                        <StyledWrapper {...styles.heading}>{fields.heading}</StyledWrapper>
                        <StyledWrapper>{parse(fields.description, parserOptions)}</StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </Hidden>
            <Hidden above="md">
                <StyledWrapper {...styles.mobileContainer}>
                    <StyledWrapper {...styles.mobileHeading}>{fields.heading}</StyledWrapper>
                    <LazyImage src={fields.img} {...styles.ContentImg} altText={fields.heading || "Benefits heading"} />
                    <StyledWrapper {...styles.mobileDescription}>
                        {parse(fields.description, parserOptions)}
                    </StyledWrapper>
                </StyledWrapper>
            </Hidden>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: SurCareerBenefits,
    definition: {
        group: "Basic",
        displayName: "SurCareerBenefits",
        fieldDefinitions: [
            {
                name: fields.heading,
                displayName: "Heading",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "text tip",
                placeholder: "text placeholder",
            },
            {
                fieldType: "Translatable",
                name: fields.img,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.description,
                editorTemplate: "RichTextField",
                displayName: "Decription",
                defaultValue: "",
            },
            {
                name: fields.imageAligment,
                fieldType: "General",
                displayName: "Image Aligment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "row" },
                    { displayName: "Right", value: "row-reverse" },
                ],
                defaultValue: "Left",
            },
        ],
    },
};
export default widgetModule;
